import React, { useState } from 'react';
import { ClipboardDocumentIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { usePageTitle } from '../hooks/usePageTitle';
import { useAiStatus, refreshAiStatus } from '../hooks/useAiStatus';
import { useToast } from '../contexts/ToastContext';
import { aiAPI } from '../services/api';
import { getErrorMessage } from '../utils/errorMessage';
import AiButton from '../components/ai/AiButton';
import AgeGroupSelect from '../components/common/AgeGroupSelect';
import HelpHint from '../components/help/HelpHint';
import type { AiAskResult } from '../types/ai';

const QUESTION_EXAMPLES = [
  'Kik játszottak 200 percnél kevesebbet idén?',
  'Kinek jár le a sportorvosija a következő 30 napban?',
  'Ki hiányzott sokat edzésről az elmúlt hónapban?',
  'Kinek lesz születésnapja a jövő héten?',
  'Kik betegek most?',
];

const PERIOD_OPTIONS = [
  { days: 7, label: 'Elmúlt hét' },
  { days: 14, label: 'Elmúlt két hét' },
  { days: 30, label: 'Elmúlt hónap' },
];

const card = 'bg-white shadow-sm rounded-xl overflow-hidden';
const inputClass =
  'w-full rounded-md border border-gray-300 px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

/**
 * Az AI asszisztens oldala a stábnak: kérdések a klub adataira (előre megírt,
 * jogosultság szerint szűrt lekérdezésekkel) és heti edzői összefoglaló.
 */
const AiAssistant: React.FC = () => {
  usePageTitle('AI asszisztens');
  const ai = useAiStatus();
  const { showToast } = useToast();

  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [answer, setAnswer] = useState<AiAskResult | null>(null);
  const [askError, setAskError] = useState<string | null>(null);

  const [ageGroupId, setAgeGroupId] = useState<number | null>(null);
  const [days, setDays] = useState(7);
  const [summarizing, setSummarizing] = useState(false);
  const [summary, setSummary] = useState('');
  const [summaryError, setSummaryError] = useState<string | null>(null);

  const ask = async (q: string) => {
    if (!q.trim()) return;
    setQuestion(q);
    setAsking(true);
    setAskError(null);
    setAnswer(null);
    try {
      setAnswer(await aiAPI.ask(q.trim()));
    } catch (err) {
      setAskError(getErrorMessage(err, 'Az AI most nem tudott válaszolni.'));
    } finally {
      setAsking(false);
      refreshAiStatus();
    }
  };

  const summarize = async () => {
    setSummarizing(true);
    setSummaryError(null);
    try {
      setSummary((await aiAPI.coachSummary({ ageGroupId, days })).content);
    } catch (err) {
      setSummaryError(getErrorMessage(err, 'Az AI most nem tudott összefoglalót írni.'));
    } finally {
      setSummarizing(false);
      refreshAiStatus();
    }
  };

  const copySummary = async () => {
    try {
      await navigator.clipboard.writeText(summary);
      showToast('Az összefoglaló a vágólapra került.', 'success');
    } catch {
      showToast('A másolás nem sikerült - jelöld ki és másold kézzel.', 'error');
    }
  };

  return (
    <div className="max-w-3xl">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-2">
        <SparklesIcon className="h-7 w-7 text-violet-600" />
        <h1 className="text-2xl font-bold text-gray-900">AI asszisztens</h1>
      </div>
      <p className="text-sm text-gray-500 mb-6">
        {ai.available
          ? `Ma még ${ai.remainingToday} AI kérésed maradt.`
          : 'Az AI funkciók most nem elérhetők. Az admin a Rendszerbeállításokban kapcsolhatja be őket.'}
      </p>

      <section className={card}>
        <div className="px-4 md:px-6 py-4 border-b border-gray-100 flex items-center gap-1.5">
          <h2 className="text-base font-semibold text-gray-900">Kérdezz az adatokról</h2>
          <HelpHint id="ai.ask" />
        </div>
        <div className="p-4 md:p-6 space-y-3">
          <form
            className="flex flex-col gap-2 sm:flex-row"
            onSubmit={(e) => {
              e.preventDefault();
              ask(question);
            }}
          >
            <input
              type="text"
              maxLength={500}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="pl. Kik játszottak 200 percnél kevesebbet tavasszal?"
              className={inputClass}
              aria-label="Kérdés"
              disabled={!ai.available}
            />
            <AiButton variant="solid" type="submit" loading={asking} loadingText="Keresem…" disabled={!ai.available || !question.trim()} className="justify-center">
              Kérdezek
            </AiButton>
          </form>
          <div className="flex flex-wrap gap-1.5">
            {QUESTION_EXAMPLES.map((example) => (
              <button
                key={example}
                type="button"
                onClick={() => ask(example)}
                disabled={!ai.available || asking}
                className="rounded-full bg-gray-100 px-2.5 py-1 text-left text-xs text-gray-700 hover:bg-gray-200 disabled:opacity-50"
              >
                {example}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-500">
            Az AI csak a kérdésedet látja, az adatokat nem: lefordítja egy előre megírt lekérdezésre, amit a Portero a te
            jogosultságaiddal futtat.
          </p>

          {askError && (
            <p className="rounded-md bg-red-50 p-3 text-sm text-red-700" role="alert">
              {askError}
            </p>
          )}

          {answer && (
            <div className="space-y-3 border-t border-gray-100 pt-3">
              {answer.queryTitle && (
                <div className="text-xs font-medium uppercase tracking-wide text-violet-700">{answer.queryTitle}</div>
              )}
              <p className="text-sm text-gray-800">{answer.answer}</p>
              {answer.rows.length > 0 && (
                <div className="overflow-x-auto rounded-lg ring-1 ring-gray-200">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        {answer.columns.map((c) => (
                          <th key={c} className="whitespace-nowrap px-3 py-2 text-left font-medium text-gray-600">
                            {c}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {answer.rows.map((row, i) => (
                        <tr key={i}>
                          {row.map((cell, j) => (
                            <td key={j} className={`px-3 py-2 text-gray-800 ${j === 0 ? 'font-medium' : 'whitespace-nowrap'}`}>
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <section className={`${card} mt-6`}>
        <div className="px-4 md:px-6 py-4 border-b border-gray-100 flex items-center gap-1.5">
          <h2 className="text-base font-semibold text-gray-900">Heti összefoglaló</h2>
          <HelpHint id="ai.weekly" />
        </div>
        <div className="p-4 md:p-6 space-y-3">
          <p className="text-sm text-gray-500">
            Részvétel, terhelés (RPE), visszajelzések, távollétek és a jövő hét programja - az AI kiemeli, kire figyelj,
            és javaslatot tesz a következő hétre.
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <span className="mb-1 block text-sm font-medium text-gray-700">Korosztály</span>
              <AgeGroupSelect value={ageGroupId} onChange={(id) => setAgeGroupId(id)} placeholder="Az összes korosztályom" />
            </div>
            <label className="block text-sm font-medium text-gray-700">
              Időszak
              <select value={days} onChange={(e) => setDays(Number(e.target.value))} className={`mt-1 ${inputClass}`}>
                {PERIOD_OPTIONS.map((o) => (
                  <option key={o.days} value={o.days}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="flex justify-end">
            <AiButton variant="solid" onClick={summarize} loading={summarizing} loadingText="Összegzem…" disabled={!ai.available}>
              {summary ? 'Újra' : 'Összefoglaló készítése'}
            </AiButton>
          </div>
          {summaryError && (
            <p className="rounded-md bg-red-50 p-3 text-sm text-red-700" role="alert">
              {summaryError}
            </p>
          )}
          {summary && (
            <div className="space-y-2 border-t border-gray-100 pt-3">
              <div className="whitespace-pre-wrap text-sm leading-relaxed text-gray-800">{summary}</div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={copySummary}
                  className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <ClipboardDocumentIcon className="h-4 w-4" />
                  Másolás
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AiAssistant;
